import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/*
 * TopShelf: product-only, field-scoped full-page search.
 *
 * Shopify's full /search engine ignores options[fields] (it always searches the
 * product description/body). To exclude descriptions we rewrite the submitted
 * query into Shopify's field-scoped syntax, e.g. "pump" ->
 * (title:pump OR product_type:pump OR vendor:pump OR tag:pump OR variants.sku:pump OR variants.title:pump)
 * This is applied only on submit (navigation to /search); the predictive
 * dropdown keeps using resources[options][fields], which IS honored.
 * On the results page we restore the human term into the visible search box.
 *
 * PARTIAL (PREFIX) MATCHING
 * A field-scoped clause on /search only ever matches whole words, and nothing
 * widens it: measured against this store, "title:Acantho" returns 0 results
 * with a trailing "*", with options[prefix]=last, and with both. Removing the
 * field scope does give partial matches, but it also brings back description
 * matching - a bare "Acantho" returns 63 products, only 9 of which are
 * Acanthophyllia - which is what this rewrite exists to prevent.
 *
 * So the partial word is completed before the query is built. suggest.json is
 * the same field-scoped, prefix-matching endpoint that powers the dropdown, so
 * its response for the current term is mined for words that the typed tokens
 * are a prefix of, and each one is added as an alternative for that token.
 * "Acantho" then searches acantho OR acanthophyllia across the scoped fields
 * and lands on the products the dropdown already shows. Completions come from
 * every scoped field, not just titles - "euphy" only reaches the torch corals
 * because Euphyllia is one of their tags. If suggest.json fails or is slow the
 * whole-word scoped query is used, which is the behavior without completion.
 */
const TOPSHELF_SCOPE_FIELDS = ['title', 'product_type', 'vendor', 'tag', 'variants.sku', 'variants.title'];
/** Below three characters nearly every word in the catalog is a completion, so don't try. */
const TOPSHELF_MIN_COMPLETION_LENGTH = 3;
/** Alternatives added per token, to keep the rewritten query a sane length. */
const TOPSHELF_MAX_COMPLETIONS = 4;
/** How long submitting a search waits on suggest.json before giving up on completion. */
const TOPSHELF_COMPLETION_TIMEOUT_MS = 700;

function topshelfIsScopedQuery(q) {
  return /\b(?:title|product_type|vendor|tag|variants\.(?:title|sku)|body):/i.test(q || '');
}

function topshelfTokenize(raw) {
  return (raw || '')
    .split(/\s+/)
    .map((t) => t.replace(/[()"*~\\:]/g, '').replace(/^-+/, '').trim())
    .filter(Boolean);
}

/**
 * Every word suggest.json matched for this term, in relevance order.
 * @param {string} raw - The term the shopper typed.
 * @returns {Promise<Set<string>>} Lowercased words to complete tokens against.
 */
async function topshelfCompletionVocabulary(raw) {
  const base = (window.Theme && Theme.routes && Theme.routes.predictive_search_url) || '/search/suggest';
  const url = new URL(`${base}.json`, location.origin);
  url.searchParams.set('q', raw);
  url.searchParams.set('resources[type]', 'query,product');
  url.searchParams.set('resources[limit]', '10');
  url.searchParams.set('resources[options][prefix]', 'last');
  url.searchParams.set('resources[options][unavailable_products]', 'last');
  url.searchParams.set('resources[options][fields]', TOPSHELF_SCOPE_FIELDS.join(','));

  const response = await fetch(url, { signal: AbortSignal.timeout(TOPSHELF_COMPLETION_TIMEOUT_MS) });
  if (!response.ok) throw new Error(`suggest.json responded ${response.status}`);
  const results = (await response.json())?.resources?.results ?? {};

  const phrases = (results.queries ?? []).map((query) => query.text);
  for (const product of results.products ?? []) {
    phrases.push(
      product.title,
      product.type,
      product.vendor,
      ...(product.tags ?? []),
      ...(product.variants ?? []).map((variant) => variant.title)
    );
  }

  const vocabulary = new Set();
  for (const phrase of phrases) {
    for (const word of String(phrase ?? '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)) {
      if (word) vocabulary.add(word);
    }
  }
  return vocabulary;
}

/**
 * The token plus the words it is a prefix of, as alternatives for one AND group.
 * @param {string} token - A single typed token.
 * @param {Set<string> | null} vocabulary - Words from {@link topshelfCompletionVocabulary}.
 * @returns {string[]} The token first, then its completions.
 */
function topshelfCompleteToken(token, vocabulary) {
  const typed = token.toLowerCase();
  if (!vocabulary || typed.length < TOPSHELF_MIN_COMPLETION_LENGTH) return [typed];

  const completions = [];
  for (const word of vocabulary) {
    if (word.length > typed.length && word.startsWith(typed)) completions.push(word);
    if (completions.length === TOPSHELF_MAX_COMPLETIONS) break;
  }
  return [typed, ...completions];
}

function topshelfScopeSearchTerm(raw, vocabulary) {
  const term = (raw || '').trim();
  if (!term) return '';
  if (topshelfIsScopedQuery(term)) return term;
  const tokens = topshelfTokenize(term);
  if (!tokens.length) return term;
  // Each token is its own AND group, opening with the typed word on `title` so
  // the search box can recover it from the URL.
  return tokens
    .map((tok) => {
      const clauses = topshelfCompleteToken(tok, vocabulary).flatMap((alt) =>
        TOPSHELF_SCOPE_FIELDS.map((f) => `${f}:${alt}`)
      );
      return '(' + clauses.join(' OR ') + ')';
    })
    .join(' AND ');
}

function topshelfScopedSearchHref(raw, vocabulary) {
  const base = (window.Theme && Theme.routes && Theme.routes.search_url) || '/search';
  const url = new URL(base, location.origin);
  url.searchParams.set('q', topshelfScopeSearchTerm(raw, vocabulary));
  url.searchParams.set('type', 'product');
  url.searchParams.set('options[prefix]', 'last');
  url.searchParams.set('options[unavailable_products]', 'last');
  return url.toString();
}

async function topshelfGoToScopedSearch(raw) {
  let vocabulary = null;
  try {
    vocabulary = await topshelfCompletionVocabulary(raw);
  } catch {
    // Completion is an enhancement; a whole-word scoped query is still correct.
  }
  window.location.href = topshelfScopedSearchHref(raw, vocabulary);
}

// Recover the human-readable term from a scoped query so the search box doesn't
// show the raw DSL. Survives pagination/facets because it reads from `q`.
// Each group's first bare `title:` clause is the typed word; the ones after it
// are completions, and `variants.title:` is excluded by requiring a boundary.
function topshelfDisplayTermFromQuery(q) {
  if (!q || !/title:/i.test(q)) return null;
  const terms = [];
  for (const group of q.split(/\s+AND\s+/i)) {
    const m = group.match(/(?:^|[(\s])title:([^\s*)]+)/i);
    if (m && m[1]) terms.push(m[1]);
  }
  return terms.length ? terms.join(' ') : null;
}

if (typeof window !== 'undefined' && !window.__topshelfSearchScopeBound) {
  window.__topshelfSearchScopeBound = true;

  // Intercept any search form submit (header search, modal "View all", results
  // page input) and route to the field-scoped URL.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!/search/i.test(action)) return;
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const raw = (input.value || '').trim();
      if (!raw || topshelfIsScopedQuery(raw)) return;
      event.preventDefault();
      topshelfGoToScopedSearch(raw);
    },
    true
  );

  // On the results page, show the human term (not the DSL) in the search box.
  const topshelfRestoreTerm = () => {
    const q = new URLSearchParams(location.search).get('q');
    const term = topshelfDisplayTermFromQuery(q);
    if (!term) return;
    document.querySelectorAll('input[name="q"]').forEach((el) => {
      if (el instanceof HTMLInputElement) el.value = term;
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', topshelfRestoreTerm);
  } else {
    topshelfRestoreTerm();
  }
  setTimeout(topshelfRestoreTerm, 0);
  window.addEventListener('pageshow', topshelfRestoreTerm);
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          topshelfGoToScopedSearch(this.refs.searchInput.value);
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit_scope]', 'each');
    // Ask for the API maximum rather than the five cards each row shows: the Glossary row
    // filters pages down to glossary/guide hubs, so it needs headroom to fill up.
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][prefix]', 'last');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
